import { driver } from 'driver.js';
import type { DriveStep } from 'driver.js';
import { useCallback } from 'react';

import 'driver.js/dist/driver.css';

import type { OrderCreationStepKey } from '@/util/vexo';

import {
  ENTRY_IMAGE_TOUR_STEPS,
  ENTRY_TOUR_STEPS,
  ORDERFLOW_TOUR_STEPS,
} from '../tour/tourSteps';
import type { TourStep } from '../tour/tourSteps';

import '../tour/orderflowTour.css';

/** True unless `element` is a CSS selector that matches nothing in the DOM. */
const isAnchorPresent = (element: unknown): boolean => {
  if (typeof element !== 'string') return true;
  return document.querySelector(element) != null;
};

/**
 * Resolve one step against the live DOM:
 * - anchor present → keep as-is;
 * - anchor missing + `keepWhenAnchorMissing` → keep the popover but drop the
 *   element, so driver.js renders it centered (used by the first-entry image
 *   tour, where the crop demonstrates a section the user hasn't unlocked yet);
 * - anchor missing otherwise → drop the step.
 */
const resolveStep = (step: TourStep): DriveStep | null => {
  if (isAnchorPresent(step.element)) return step;
  if (!step.keepWhenAnchorMissing) return null;
  const { element: _element, keepWhenAnchorMissing: _keep, ...rest } = step;
  return rest;
};

/**
 * Bring a highlighted anchor to the middle of its scroll container before the
 * popover renders.
 *
 * The wizard is a fixed full-viewport shell (`WizardShell`) whose body is the
 * scroller, so the window itself never scrolls: anchors below the fold (the
 * directions field and its Browse saved link on the job-type step) would
 * otherwise be highlighted off-screen. `instant` is deliberate — driver.js
 * measures the element right after this, so an animated scroll would place the
 * popover against a stale position.
 */
const centerAnchor = (element?: Element) => {
  if (!element || typeof element.scrollIntoView !== 'function') return;
  element.scrollIntoView({
    behavior: 'instant',
    block: 'center',
    inline: 'nearest',
  });
};

/**
 * Drives a driver.js tour over the given steps, first dropping any whose anchor
 * is not currently mounted (markup-only, LTA, substep- or view-specific
 * elements). An empty result is a no-op, so the tour never opens an overlay
 * pointing at nothing.
 */
const driveSteps = (allSteps: TourStep[], popoverClass = 'orderflow-tour') => {
  const steps = allSteps
    .map(resolveStep)
    .filter((step): step is DriveStep => step != null);
  if (steps.length === 0) return;

  const tour = driver({
    showProgress: true,
    allowClose: true,
    overlayColor: 'rgba(28, 28, 28, 0.6)',
    popoverClass,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Got it',
    steps,
    onHighlightStarted: (element) => centerAnchor(element),
    // The scroller is inside the fixed wizard shell, so its scroll events never
    // reach driver.js's window listener — realign the overlay ourselves once
    // the step is on screen.
    onHighlighted: () => tour.refresh(),
  });
  tour.drive();
};

/**
 * Contextual help tours for the OrderFlow V2 flow.
 *
 * - `startTour(stepKey)` runs the tour for the current wizard step (a `null`
 *   key is a no-op).
 * - `startEntryTour()` runs the text-only tour for the pre-wizard entry/landing
 *   screen at `/orders-v2/create` (the "?" help button).
 * - `startFirstEntryTour()` runs the richer, image-annotated variant used for
 *   the automatic first-visit onboarding.
 */
export function useOrderFlowTour() {
  const startTour = useCallback((stepKey: OrderCreationStepKey | null) => {
    if (!stepKey) return;
    driveSteps(ORDERFLOW_TOUR_STEPS[stepKey] ?? []);
  }, []);

  const startEntryTour = useCallback(() => {
    driveSteps(ENTRY_TOUR_STEPS);
  }, []);

  const startFirstEntryTour = useCallback(() => {
    driveSteps(ENTRY_IMAGE_TOUR_STEPS, 'orderflow-tour orderflow-tour-image');
  }, []);

  return { startTour, startEntryTour, startFirstEntryTour };
}

export default useOrderFlowTour;
